// npm modules imports
import { UserManager, WebStorageStateStore, Log } from "oidc-client-ts";
import jwt_decode from "jwt-decode";
import axios from "axios";

// app module imports
import appStore from "../store/appStore";

Log.logger = console;
Log.level = Log.INFO;

const fetchPIIBrokersAEAName = async (entityUri, redirectionTarget) => {
  const endpoint =
    appStore.state.config.configurationData.common_module.profileManagement
      .getProfileInfo;

  const payload = {
    entityUri: entityUri
  };
  await axios
    .post(endpoint, payload, {
      headers: {
        Authorization: `Bearer ${appStore.state.auth.accessToken}`
      }
    })
    .then(response => {
      const data = response.data.data;
      const name = data.name;
      appStore.commit(
        "header/setUserName",
        name ||
          appStore.state.header.userProfile.nric ||
          appStore.state.header.userProfile.sgID
      );
      window.location.href = redirectionTarget;
    })
    .catch(() => {
      window.location.href = redirectionTarget;
    });
};

/**
 * This method is used to modify keycloak/oidc webstorage store to support
 * token exchange feature
 *
 * Original token is saved in vuex persisted state then when user want to
 * switch back to who he/she is, original token will be fetched and injected
 * to modified user storage (user storage modified by impersonated user)
 */
const userStorageModifier = configurationData => {
  const userStorage = JSON.parse(
    localStorage.getItem(
      `oidc.user:${configurationData.commonConfig.idpAuthority}:${configurationData.commonConfig.idpClientId}`
    )
  );
  userStorage["access_token"] = appStore.state.auth.originToken.accessToken;
  userStorage["refresh_token"] = appStore.state.auth.originToken.refreshToken;
  userStorage["id_token"] = "";
  userStorage["profile"] = {};

  localStorage.setItem(
    `oidc.user:${configurationData.commonConfig.idpAuthority}:${configurationData.commonConfig.idpClientId}`,
    JSON.stringify(userStorage)
  );
};

export default class SecurityService {
  configurationData;
  mgr;

  constructor(configurationData) {
    this.configurationData = configurationData;

    this.mgr = new UserManager({
      userStore: new WebStorageStateStore(),
      authority: configurationData.commonConfig.idpAuthority,
      client_id: configurationData.commonConfig.idpClientId,
      redirect_uri: configurationData.commonConfig.idpRedirectUri,
      post_logout_redirect_uri:
        configurationData.commonConfig.idpPostLogoutRedirectUri,
      silent_redirect_uri: configurationData.commonConfig.idpSilentRedirectUri,
      response_mode: "fragment",
      response_type: configurationData.commonConfig.idpResponseType,
      scope: configurationData.commonConfig.idpScope,
      silentRequestTimeout: configurationData.commonConfig.idpSilentReqTimeout,
      accessTokenExpiringNotificationTime:
        configurationData.commonConfig.idpAccessTokenExpiringNotificationTime,
      loadUserInfo: true,
      automaticSilentRenew: false
    });
  }

  get mgr() {
    return this.mgr;
  }

  exchangeToken(nric, uen) {
    const md5 = require("js-md5");
    let hashedSubject;

    if (uen) {
      hashedSubject = md5(`${uen}-${nric}`) + "@corppass";
    } else {
      hashedSubject = md5(`${nric}`) + "@singpass";
    }

    let self = this;

    appStore.dispatch("setApplicationLoadingToTrue");
    const initializedMgr = this.mgr;

    self.getUser().then(async resp => {
      // When user get back into their account

      if (appStore.state.header.isImpersonating) {
        appStore.commit("header/setIsImpersonating", false);
        appStore.commit("header/setCanImpersonate", true);
        await self.clearSession(
          this.configurationData.commonConfig.idpClientId,
          resp.refresh_token
        );

        userStorageModifier(this.configurationData);

        initializedMgr
          .signinSilent()
          .then(async user => {
            appStore.commit("auth/setOriginToken", {
              accessToken: user.access_token,
              refreshToken: user.refresh_token
            });

            const decodedToken = jwt_decode(user.access_token);
            appStore.commit("auth/setAccessToken", user.access_token);

            appStore.commit(
              "header/setAdditionalProfileProperties",
              user.profile
            );
            appStore.commit(
              "header/setRoles",
              typeof decodedToken.roles === "string"
                ? [decodedToken.roles]
                : decodedToken.roles
                ? [...decodedToken.roles]
                : []
            );

            appStore.commit(
              "header/setUserName",
              user.profile.nric || user.profile.identity_number
            );

            if (
              appStore.state.header.userProfile.roles.includes("pii-brokers")
            ) {
              appStore.commit("header/setHasProfileTo", true);
              await fetchPIIBrokersAEAName(
                "/profile/pii-broker",
                "/aceas/PII-management"
              );
            } else if (
              appStore.state.header.userProfile.roles.includes("aea")
            ) {
              appStore.commit("header/setHasProfileTo", true);
              await fetchPIIBrokersAEAName(
                "/exam/profiles",
                "/aceas/RES-exam-management"
              );
            } else {
              await self.validateProfile();
              // location.reload();
              window.location.href = "/aceas/";
            }
          })
          .catch(function(err) {
            window.Vue.prototype.$buefy.snackbar.open({
              duration: 2000,
              message: err.message,
              type: "is-danger",
              position: "is-top",
              actionText: "✖"
            });
            appStore.dispatch("setApplicationLoadingToFalse");
          });
      } else {
        const ACCESS_TOKEN = await self.getAcessToken();
        const clientId = this.configurationData.commonConfig.idpClientId;
        const signInEndpoint = `${this.configurationData.commonConfig.idpAuthority}/protocol/openid-connect/token`;

        const body = new URLSearchParams();
        body.append("client_id", clientId);
        body.append(
          "grant_type",
          "urn:ietf:params:oauth:grant-type:token-exchange"
        );
        body.append(
          "subject_token_type",
          "urn:ietf:params:oauth:token-type:access_token"
        );
        body.append("subject_token", ACCESS_TOKEN);
        body.append("audience", clientId);
        body.append("requested_subject", hashedSubject);
        body.append("refresh_token", appStore.state.auth.refreshToken);
        body.append("scope", resp.scope);

        await axios
          .post(signInEndpoint, body, {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: `Bearer ${ACCESS_TOKEN}`
            }
          })
          .then(async response => {
            appStore.commit("auth/setAccessToken", response.data.access_token);
            const updatedUser = {
              ...resp,
              id_token: response.data.id_token,
              access_token: response.data.access_token,
              refresh_token: response.data.refresh_token,
              session_state: response.data.session_state,
              expires_in: response.data.expires_in
            };
            await initializedMgr
              .storeUser({
                updatedUser,
                toStorageString: () => JSON.stringify(updatedUser)
              })
              .then(async () => {
                await this.mgr.events.load(updatedUser);

                initializedMgr.getUser().then(async user => {
                  const decodedToken = jwt_decode(user.access_token);
                  appStore.commit("header/setIsImpersonating", true);
                  appStore.commit("header/setCanImpersonate", false);
                  appStore.commit("auth/setAccessToken", user.access_token);
                  appStore.commit(
                    "header/setRoles",
                    typeof decodedToken.roles === "string"
                      ? [decodedToken.roles]
                      : [...decodedToken.roles]
                  );

                  await self.populateUserInfo();
                });
              });
          })
          .catch(function(err) {
            window.Vue.prototype.$buefy.snackbar.open({
              duration: 2000,
              message: err.message,
              type: "is-danger",
              position: "is-top",
              actionText: "✖"
            });
            appStore.dispatch("setApplicationLoadingToFalse");
          });
      }
    });
  }

  async clearSession(clientId, refreshToken) {
    const endpoint = `${this.configurationData.commonConfig.idpAuthority}/protocol/openid-connect/logout`;
    const params = new URLSearchParams();
    params.append("client_id", clientId);
    params.append("refresh_token", refreshToken);

    await axios
      .post(endpoint, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${appStore.state.auth.accessToken}`
        }
      })
      .catch(err => {
        window.Vue.prototype.$buefy.snackbar.open({
          duration: 2000,
          message: err.message,
          type: "is-danger",
          position: "is-top",
          actionText: "✖"
        });
        appStore.dispatch("setApplicationLoadingToFalse");
      });
  }

  async validateProfile() {
    const endpoint = appStore.state.config.configurationData.common_module;

    const payload = {
      sgId: appStore.state.header.userProfile.sgID
    };
    await axios
      .post(
        endpoint.profileManagement.getHasProfileValidationEndpoint,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${appStore.state.auth.accessToken}`
          }
        }
      )
      .then(response => {
        const hasProfile =
          response.data.data.id &&
          !response.data.data.draftFlag &&
          response.data.data.modifiedByMopFlag
            ? true
            : false;

        appStore.commit("header/setIsValidated", true);

        if (hasProfile) {
          appStore.commit("header/setHasProfileTo", true);
        } else {
          appStore.commit("header/setHasProfileTo", false);
        }
      })
      .catch(() => {
        appStore.commit("header/setHasProfileTo", true);
        appStore.commit("header/setIsValidated", false);
      });
  }

  async populateUserInfo() {
    const userInfoEndpoint = `${this.configurationData.commonConfig.idpAuthority}/user-info`;
    const self = this;
    await axios
      .get(userInfoEndpoint, {
        headers: {
          Authorization: `Bearer ${appStore.state.auth.accessToken}`
        }
      })
      .then(async function(response) {
        const profile = response.data;
        appStore.commit("header/setAdditionalProfileProperties", profile);

        appStore.commit(
          "header/setUserName",
          profile.nric || profile.identity_number
        );

        if (appStore.state.header.userProfile.roles.includes("pii-brokers")) {
          appStore.commit("header/setHasProfileTo", true);
          await fetchPIIBrokersAEAName(
            "/profile/pii-broker",
            "/aceas/PII-management"
          );
        } else if (appStore.state.header.userProfile.roles.includes("aea")) {
          appStore.commit("header/setHasProfileTo", true);
          await fetchPIIBrokersAEAName(
            "/exam/profiles",
            "/aceas/RES-exam-management"
          );
        } else {
          await self.validateProfile();
          // location.reload();
          window.location.href = "/aceas/";
        }
      })
      .catch(err => {
        window.Vue.prototype.$buefy.snackbar.open({
          duration: 2000,
          message: err.message,
          type: "is-danger",
          position: "is-top",
          actionText: "✖"
        });
        appStore.dispatch("setApplicationLoadingToFalse");
      });
  }

  // Renew the token manually
  renewToken() {
    let self = this;
    const initializedMgr = this.mgr;
    return new Promise((resolve, reject) => {
      initializedMgr
        .signinSilent()
        .then(function(user) {
          appStore.commit("auth/setAccessToken", user.access_token);
          if (user == null) {
            self.signIn(null);
          } else {
            return resolve(user);
          }
        })
        .catch(function(err) {
          return reject(err);
        });
    });
  }

  // Clear abandoned auth request
  clearStaleState() {
    const initializedMgr = this.mgr;
    initializedMgr.clearStaleState();
  }

  // Get the user who is logged in
  getUser() {
    let self = this;
    const initializedMgr = this.mgr;
    return new Promise((resolve, reject) => {
      initializedMgr
        .getUser()
        .then(function(user) {
          if (user == null) {
            self.signIn();
            return resolve(null);
          } else {
            return resolve(user);
          }
        })
        .catch(function(err) {
          return reject(err);
        });
    });
  }

  // Check if there is any user logged in
  getSignedIn(nextRedirection, kcIdpHint = null) {
    let self = this;
    const initializedMgr = this.mgr;
    return new Promise((resolve, reject) => {
      initializedMgr
        .getUser()
        .then(function(user) {
          if (user == null) {
            self.signIn(nextRedirection, kcIdpHint);
            return resolve(false);
          } else {
            appStore.commit("auth/setSignedIn", true);
            appStore.commit("auth/setIsSessionActive", true);
            return resolve(true);
          }
        })
        .catch(function(err) {
          return reject(err);
        });
    });
  }

  // Redirect of the current window to the authorization endpoint.
  signIn(nextRedirection, kcIdpHint) {
    const initializedMgr = this.mgr;
    initializedMgr
      .signinRedirect({
        state: { path: nextRedirection },
        ...(kcIdpHint && {
          extraQueryParams: {
            kc_idp_hint: kcIdpHint
          }
        })
      })
      .catch(function(err) {
        window.Vue.prototype.$buefy.snackbar.open({
          duration: 2000,
          message: err.message,
          type: "is-danger",
          position: "is-top",
          actionText: "✖"
        });
        appStore.dispatch("setApplicationLoadingToFalse");
      });
  }

  // Redirect of the current window to the end session endpoint
  signOut() {
    appStore.dispatch("setApplicationLoadingToTrue");
    const initializedMgr = this.mgr;
    this.clearStaleState();

    appStore.commit("auth/setSignedIn", false);
    appStore.commit("auth/setAccessToken", "");
    appStore.commit("auth/setIsReminded", false);
    appStore.commit("auth/setIsSessionActive", false);
    appStore.commit("auth/setWasIdle", false);
    appStore.commit("auth/setLoginTime", null);
    appStore.commit("header/setUserName", "");
    appStore.commit("header/setRoles", []);
    appStore.commit("survey/setIsSurveyMandatory", false);
    appStore.commit("survey/setHasFilledSurvey", false);
    appStore.commit("header/setHasProfileTo", true);
    appStore.commit("auth/setOriginToken", {
      accessToken: "",
      refreshToken: ""
    });
    appStore.commit("header/setCanImpersonate", false);
    appStore.commit("header/setIsImpersonating", false);

    // remove cookie
    document.cookie = "timeInMs=;expires=Thu, 01 Jan 1970 00:00:00 UTC;";
    document.cookie =
      "lastUnloadActivity=;expires=Thu, 01 Jan 1970 00:00:00 UTC;";
    document.cookie =
      "lastRefreshActivity=;expires=Thu, 01 Jan 1970 00:00:00 UTC;";

    initializedMgr
      .signoutRedirect()
      .then(function() {
        appStore.commit("auth/setTokenCheck", false);
      })
      .catch(function(err) {
        return err;
      });
  }

  silentRenewCallback() {
    const initializedMgr = this.mgr;

    initializedMgr.signinSilentCallback().catch(function(err) {
      throw new Error(err);
    });
  }

  // Get the profile of the user logged in
  getProfile() {
    const initializedMgr = this.mgr;
    let self = this;
    return new Promise((resolve, reject) => {
      initializedMgr
        .getUser()
        .then(function(user) {
          if (user == null) {
            self.signIn();
            return resolve(null);
          } else {
            return resolve(user.profile);
          }
        })
        .catch(function(err) {
          return reject(err);
        });
    });
  }

  // Get the token id
  getIdToken() {
    const initializedMgr = this.mgr;
    let self = this;
    return new Promise((resolve, reject) => {
      initializedMgr
        .getUser()
        .then(function(user) {
          if (user == null) {
            self.signIn();
            return resolve(null);
          } else {
            return resolve(user.id_token);
          }
        })
        .catch(function(err) {
          return reject(err);
        });
    });
  }

  // Get the session state
  getSessionState() {
    const initializedMgr = this.mgr;
    let self = this;
    return new Promise((resolve, reject) => {
      initializedMgr
        .getUser()
        .then(function(user) {
          if (user == null) {
            self.signIn();
            return resolve(null);
          } else {
            return resolve(user.session_state);
          }
        })
        .catch(function(err) {
          return reject(err);
        });
    });
  }

  // Get the access token of the logged in user
  getAcessToken() {
    const initializedMgr = this.mgr;
    let self = this;
    return new Promise((resolve, reject) => {
      initializedMgr
        .getUser()
        .then(function(user) {
          if (user == null) {
            self.signIn();
            return resolve(null);
          } else {
            return resolve(user.access_token);
          }
        })
        .catch(function(err) {
          return reject(err);
        });
    });
  }

  // Takes the scopes of the logged in user
  getScopes() {
    const initializedMgr = this.mgr;
    let self = this;
    return new Promise((resolve, reject) => {
      initializedMgr
        .getUser()
        .then(function(user) {
          if (user == null) {
            self.signIn();
            return resolve(null);
          } else {
            return resolve(user.scopes);
          }
        })
        .catch(function(err) {
          return reject(err);
        });
    });
  }

  // Get the user roles logged in
  getRole() {
    const initializedMgr = this.mgr;
    let self = this;
    return new Promise((resolve, reject) => {
      initializedMgr
        .getUser()
        .then(function(user) {
          if (user == null) {
            self.signIn();
            return resolve(null);
          } else {
            return resolve(user.profile.role);
          }
        })
        .catch(function(err) {
          return reject(err);
        });
    });
  }
}

// async function runMgrEvents() {
//   const initializedMgr = await mgr();

//   await initializedMgr.events.addAccessTokenExpiring(function() {
//     console.log("token expiring...");
//     console.log("min 10s");
//   });

//   await initializedMgr.events.addAccessTokenExpired(function() {
//     const securityService = new SecurityService();
//     console.log("token expired");
//     securityService.renewToken();
//   });

//   await initializedMgr.events.addUserSignedOut(function() {
//     console.log("User signed out");
//   });
// }

// runMgrEvents();
